import React from 'react';
import {
  Sparkles, MapPin, Gauge, CheckSquare, FileText, Users, Clock,
  Settings, X, GripVertical, RefreshCw,
} from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';

const ICONS = {
  Sparkles, MapPin, Gauge, CheckSquare, FileText, Users, Clock,
};

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);

/**
 * Shared renderer for all 7 widget types.
 * Used by ProjectOverviewBuilder (mode="preview") and ProjectDetail (mode="live").
 */
const WidgetCard = ({
  catalogItem,
  data = {},
  derived = {},
  aispaceColor = '#8B5CF6',
  mode = 'live',           // 'preview' (builder) | 'live' (project page)
  isGenerating = false,
  onRefresh,
  onEditPrompt,
  onRemove,
  dragHandleProps,
}) => {
  const { t } = useTranslation();
  const Icon = ICONS[catalogItem.icon] || Sparkles;
  const isPreview = mode === 'preview';

  const Header = (
    <div className="flex items-center justify-between px-4 pt-4">
      <div className="flex items-center gap-2 min-w-0">
        {isPreview && (
          <span
            {...(dragHandleProps || {})}
            className="cursor-grab active:cursor-grabbing text-[#9CA3AF] shrink-0"
            title="Verschieben"
          >
            <GripVertical size={16} />
          </span>
        )}
        <Icon size={16} className="text-[#6B7280] shrink-0" />
        <h3 className="text-[14px] font-semibold text-[#212121] truncate">{
          // Prefer translated label via the catalog key; fall back to the
          // backend-provided label so unknown widgets still render.
          (() => {
            const translationKey = `widgets.titles.${catalogItem.key}`;
            const translated = t(translationKey);
            return translated === translationKey ? catalogItem.label : translated;
          })()
        }</h3>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isPreview && catalogItem.is_llm && onEditPrompt && (
          <button
            type="button"
            onClick={onEditPrompt}
            className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]"
            title="Prompt bearbeiten"
          >
            <Settings size={16} />
          </button>
        )}
        {isPreview && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]"
            title="Entfernen"
          >
            <X size={16} />
          </button>
        )}
        {!isPreview && catalogItem.is_llm && onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isGenerating}
            className="p-1 rounded hover:bg-[#F3F4F6] disabled:opacity-50"
            title="Neu generieren"
          >
            {isGenerating ? (
              <div className="w-4 h-4 border-2 border-[#9CA3AF] border-t-transparent rounded-full animate-spin" />
            ) : (
              <RefreshCw size={14} className="text-[#9CA3AF]" />
            )}
          </button>
        )}
      </div>
    </div>
  );

  const body = renderBody(catalogItem, data, derived, aispaceColor, mode, isGenerating, t);

  // Live map widget gets a Figma-specific layout: full-bleed map, floating
  // white card at the bottom-left with title + refresh + address. The shared
  // header/padding don't apply because the map must reach the card edges.
  const isMapLive = !isPreview && catalogItem.type === 'llm-map';
  if (isMapLive) {
    const address = data?.formattedAddress;
    const titleText = (() => {
      const translationKey = `widgets.titles.${catalogItem.key}`;
      const translated = t(translationKey);
      return translated === translationKey ? catalogItem.label : translated;
    })();
    return (
      <div
        className="relative bg-white rounded-xl shadow-sm border border-[#E5E7EB] overflow-hidden w-full"
        style={{ height: 'clamp(260px, 32vh, 360px)' }}
      >
        {/* Full-bleed map */}
        {address ? (
          <iframe
            title="Location"
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block' }}
            src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
            allowFullScreen
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#F3F4F6] text-[12px] italic text-[#9CA3AF]">
            {isGenerating ? 'Wird generiert…' : 'Noch nicht extrahiert'}
          </div>
        )}

        {/* Floating overlay card — title + refresh on top row, address below */}
        <div className="absolute left-3 right-3 bottom-3 bg-white rounded-lg shadow-md border border-[#E5E7EB] px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-[14px] font-semibold text-[#212121] truncate">{titleText}</h3>
            {catalogItem.is_llm && onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isGenerating}
                className="p-1 rounded hover:bg-[#F3F4F6] disabled:opacity-50 shrink-0"
                title="Neu generieren"
              >
                {isGenerating ? (
                  <div className="w-4 h-4 border-2 border-[#9CA3AF] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <RefreshCw size={14} className="text-[#9CA3AF]" />
                )}
              </button>
            )}
          </div>
          {address && (
            <p className="text-[13px] text-[#374151] mt-1 line-clamp-2">{address}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    // Fixed-shape card: width is driven by the parent grid (so it scales with
    // viewport) and height clamps between 260–360px depending on viewport
    // height. The body scrolls when its content exceeds that, so widgets stay
    // visually uniform regardless of how much text the LLM returns.
    <div
      className="bg-white rounded-xl shadow-sm border border-[#E5E7EB] overflow-hidden flex flex-col w-full"
      style={{ height: 'clamp(260px, 32vh, 360px)' }}
    >
      {Header}
      <div className="px-4 pb-4 pt-3 flex-1 min-h-0 flex flex-col overflow-y-auto thin-scrollbar">{body}</div>
    </div>
  );
};

// --------------------------------------------------------------------------- //

function renderBody(catalogItem, data, derived, aispaceColor, mode, isGenerating, t = (k) => k) {
  const key = catalogItem.key;
  const type = catalogItem.type;
  const isPreview = mode === 'preview';

  if (type === 'llm-text') {
    const text = data?.text;
    if (isGenerating) return <p className="text-[13px] text-[#6B7280]">Wird generiert…</p>;
    if (!text && isPreview) return <p className="text-[13px] italic text-[#9CA3AF]">Some quick example text to build on the card title and make up the bulk of the card's content.</p>;
    if (!text) return <p className="text-[13px] italic text-[#9CA3AF]">{t('widgets.notGenerated')}</p>;
    return <p className="text-[13px] text-[#374151] leading-relaxed">{text}</p>;
  }

  if (type === 'llm-map') {
    const address = data?.formattedAddress;
    if (isPreview) {
      return (
        <div className="space-y-1">
          <p className="text-[14px] font-semibold text-[#212121]">San Francisco, CA</p>
          <p className="text-[13px] text-[#6B7280]">123 Market Street, 94103</p>
        </div>
      );
    }
    return (
      // The wrapper itself must flex-fill the body, otherwise the inner map
      // div (also flex-1) has no height to expand into and the card collapses
      // to the address line, leaving white space.
      <div className="flex flex-col gap-2 flex-1 min-h-0 h-full">
        <div className="flex-1 min-h-0 rounded-lg overflow-hidden bg-[#F3F4F6]">
          {address ? (
            <iframe
              title="Location"
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
              allowFullScreen
            />
          ) : (
            <div className="h-full flex items-center justify-center text-[12px] italic text-[#9CA3AF]">
              {isGenerating ? 'Wird generiert…' : 'Noch nicht extrahiert'}
            </div>
          )}
        </div>
        {address && <p className="text-[13px] text-[#374151] truncate flex-shrink-0">{address}</p>}
      </div>
    );
  }

  if (type === 'llm-date') {
    const days = data?.days;
    const formatted = data?.formatted || data?.date;
    if (isPreview) {
      return (
        <div className="flex flex-col items-center justify-center flex-1 py-4">
          <span className="text-[48px] font-bold text-[#212121] leading-none">12</span>
          <p className="text-[12px] text-[#6B7280] mt-2">Days Remaining</p>
        </div>
      );
    }
    if (isGenerating) return <p className="text-[13px] text-[#6B7280] text-center">Wird generiert…</p>;
    if (days === null || days === undefined) {
      return <p className="text-[13px] italic text-[#9CA3AF] text-center py-4">{t('widgets.noSubmissionDate')}</p>;
    }
    if (days < 0) {
      return (
        <div className="flex flex-col items-center justify-center flex-1 py-4">
          <span className="text-[32px] font-bold text-[#DC2626] leading-none">Abgelaufen</span>
          <p className="text-[12px] text-[#6B7280] mt-2">Vor {Math.abs(days)} {Math.abs(days) === 1 ? 'Tag' : 'Tagen'}</p>
          <p className="text-[12px] font-medium text-[#212121] mt-1">{formatted}</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-4">
        <span className="text-[48px] font-bold text-[#212121] leading-none">{days}</span>
        <p className="text-[12px] text-[#6B7280] mt-2">Days Remaining</p>
        <p className="text-[12px] font-medium text-[#212121] mt-1">{formatted}</p>
      </div>
    );
  }

  if (type === 'derived-percent') {
    const pct = isPreview ? 40 : (derived?.analysis_score?.percentage ?? 0);
    // Half-circle gauge per Figma
    const r = 60;
    const circ = Math.PI * r;
    const offset = circ * (1 - pct / 100);
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-2">
        <svg width="160" height="90" viewBox="0 0 160 90">
          <path
            d={`M 20 80 A ${r} ${r} 0 0 1 140 80`}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d={`M 20 80 A ${r} ${r} 0 0 1 140 80`}
            fill="none"
            stroke={aispaceColor}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
          />
        </svg>
        <span className="text-[28px] font-bold text-[#212121] -mt-5">{pct}%</span>
      </div>
    );
  }

  if (type === 'derived-bars') {
    const previewCounts = { accepted: 5, review: 2, missing: 1, rejected: 1 };
    const counts = isPreview
      ? previewCounts
      : {
          accepted: derived?.checklist?.accepted || 0,
          review: derived?.checklist?.further_review || 0,
          missing: derived?.checklist?.info_missing || 0,
          rejected: derived?.checklist?.rejected || 0,
        };
    const bars = [
      { key: 'accepted', label: t('answerStatus.accepted'), value: counts.accepted, color: '#10B981' },
      { key: 'review', label: t('answerStatus.review'), value: counts.review, color: '#F59E0B' },
      { key: 'missing', label: t('answerStatus.missing'), value: counts.missing, color: '#F97316' },
      { key: 'rejected', label: t('answerStatus.rejected'), value: counts.rejected, color: '#EF4444' },
    ];
    const max = Math.max(1, ...bars.map(b => b.value));
    return (
      <div className="flex items-end justify-around h-[160px] gap-2 flex-1 px-2">
        {bars.map((b) => {
          const h = max > 0 ? (b.value / max) * 100 : 0;
          return (
            <div key={b.key} className="flex flex-col items-center h-full flex-1 min-w-0">
              <div className="relative w-[28px] flex-1 flex items-end">
                <div className="absolute inset-0 rounded-t-md bg-[#F3F4F6]" />
                <div
                  className="relative w-full rounded-t-md flex items-start justify-center pt-1"
                  style={{ height: `${h}%`, backgroundColor: b.color }}
                  title={`${b.label}: ${b.value}`}
                >
                  {b.value > 0 && (
                    <span className="text-[11px] font-semibold text-white">{b.value}</span>
                  )}
                </div>
              </div>
              <span
                className="text-[10px] text-[#6B7280] mt-2 text-center w-full px-0.5 truncate"
                title={b.label}
              >
                {b.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === 'derived-count') {
    const count = isPreview ? 24 : (derived?.document_count?.count ?? 0);
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-4">
        <span className="text-[48px] font-bold text-[#212121] leading-none">{count}</span>
        <p className="text-[12px] text-[#6B7280] mt-2">{t('widgets.documents')}</p>
      </div>
    );
  }

  if (type === 'lookup-users') {
    const users = isPreview
      ? [
          { name: 'Christian Dietzmann' },
          { name: 'Lasse Wrobel' },
          { name: 'Timon Jaeggi' },
        ]
      : (derived?.participants || []);
    if (!users.length) {
      return <p className="text-[13px] italic text-[#9CA3AF]">Keine Mitglieder.</p>;
    }
    return (
      <div className="space-y-2 max-h-[160px] overflow-y-auto">
        {users.map((u, i) => (
          <div key={u.email || i} className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-medium shrink-0"
              style={{ backgroundColor: aispaceColor }}
            >
              {getInitials(u.name)}
            </div>
            <span className="text-[13px] text-[#374151] truncate">{u.name}</span>
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-[13px] italic text-[#9CA3AF]">Unbekannter Widget-Typ.</p>;
}

export default WidgetCard;
